import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { Dropdown } from 'react-bootstrap';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { Modal } from 'bootstrap';
import './modal.css'; // Import custom modal CSS
import './collections-actions.css'; // Dropdown item styles
import { getOverdueCollections, getOverdueCollectionStats, useRecordPayment, getLedgerAccountById, useGetEarlyRepaymentQuote, useRecordEarlyRepayment } from '../../../services/loanService';
import type { OverdueCollection as ApiOverdueCollection } from '../../../services/schemas/loanServiceSchemas/overdueCollection';
import type { OverdueCollectionStats } from '../../../services/schemas/loanServiceSchemas/overdueCollectionStats';
import { useForm, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { LedgerAccount } from '../../../services/schemas/loanServiceSchemas/ledgerAccount';
import type { EarlyRepaymentQuote } from '../../../services/schemas/loanServiceSchemas/earlyRepaymentQuote';
import { toLocalDateString } from '../../../utils/localDate';
import PromisesToPayPanel from './PromisesToPayPanel';
import SendReminderModal, { ReminderTarget } from './SendReminderModal';

// Define types for overdue collections
interface OverdueCollection {
  id: string;
  accountNumber: string;
  customerName: string;
  loanAmount: number;
  dueAmount: number;
  dueDate: string;
  status: 'Overdue' | 'Critically Overdue' | 'Partially Paid' | 'In Recovery';
  contactNumber: string;
  email: string;
  daysOverdue: number;
  lastPaymentDate: string;
  lastPaymentAmount: number;
}

// Define payment form schema
const paymentFormSchema = z.object({
  paymentAmount: z.string()
      .min(1, 'Payment amount is required')
      .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
        message: 'Payment amount must be greater than 0'
      }),
  valueDate: z.string()
      .min(1, 'Payment date is required')
      .refine((val) => !isNaN(Date.parse(val)), {
        message: 'Please select a valid date'
      }),
  externalRef: z.string().optional(),
  remarks: z.string().optional()
});

type PaymentFormData = z.infer<typeof paymentFormSchema>;

// Add early repayment form schema
const earlyRepaymentFormSchema = z.object({
  valueDate: z.string()
      .min(1, 'Payment date is required')
      .refine((val) => !isNaN(Date.parse(val)), {
        message: 'Please select a valid date'
      }),
  externalRef: z.string().optional(),
  remarks: z.string().optional()
});

type EarlyRepaymentFormData = z.infer<typeof earlyRepaymentFormSchema>;

const OverdueCollections = () => {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const ALL_OVERDUE_PAGE_SIZE = 10000;
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<ApiOverdueCollection | null>(null);
  // Send Reminder: the loan the reminder dialog is open for
  const [reminderTarget, setReminderTarget] = useState<ReminderTarget | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [filterDaysOverdue, setFilterDaysOverdue] = useState<number | undefined>(undefined);
  const [customerName, setCustomerName] = useState('');
  const [stats, setStats] = useState<OverdueCollectionStats>({
    total: 0,
    overdue: 0,
    critical: 0,
    recovery: 0,
    partial: 0
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showEarlyRepaymentModal, setShowEarlyRepaymentModal] = useState(false);
  const [loanAccount, setLoanAccount] = useState<LedgerAccount | null>(null);
  const [earlyRepaymentCalculation, setEarlyRepaymentCalculation] = useState<EarlyRepaymentQuote | null>(null);
  const [isCalculatingEarlyRepayment, setIsCalculatingEarlyRepayment] = useState(false);
  const [alert, setAlert] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'info';
    message: string;
  }>({
    show: false,
    type: 'info',
    message: ''
  });
  const navigate = useNavigate();

  // Payment mutation
  const recordPaymentMutation = useRecordPayment({
    mutation: {
      onSuccess: () => {
        toast.success('Payment processed successfully');
        fetchOverdueCollections();
        fetchStats();
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to process payment');
      }
    }
  });

  // Early repayment quote mutation
  const getEarlyRepaymentQuoteMutation = useGetEarlyRepaymentQuote({
    mutation: {
      onSuccess: (data) => {
        setEarlyRepaymentCalculation(data);
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to calculate early repayment');
        setIsCalculatingEarlyRepayment(false);
      }
    }
  });

  // Early repayment mutation
  const recordEarlyRepaymentMutation = useRecordEarlyRepayment({
    mutation: {
      onSuccess: () => {
        toast.success('Early repayment processed successfully');
        fetchOverdueCollections();
        fetchStats();
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to process early repayment');
      }
    }
  });

  // State for collections data
  const [overdueCollections, setOverdueCollections] = useState<{
    content: ApiOverdueCollection[];
    totalElements: number;
  }>({
    content: [],
    totalElements: 0
  });

  // Initialize form
  const methods = useForm<PaymentFormData>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      paymentAmount: undefined,
      valueDate: toLocalDateString(),
      externalRef: '',
      remarks: ''
    }
  });

  const { register, handleSubmit, formState: { errors }, reset } = methods;

  // Initialize early repayment form
  const earlyRepaymentMethods = useForm<EarlyRepaymentFormData>({
    resolver: zodResolver(earlyRepaymentFormSchema),
    defaultValues: {
      valueDate: toLocalDateString(),
      externalRef: '',
      remarks: ''
    }
  });

  const { register: registerEarlyRepayment, handleSubmit: handleSubmitEarlyRepayment, formState: { errors: earlyRepaymentErrors }, reset: resetEarlyRepayment } = earlyRepaymentMethods;

  // Dummy data for overdue collections
  const [overdueCollectionsData, setOverdueCollectionsData] = useState<{
    content: OverdueCollection[];
    totalElements: number;
  }>({
    content: [
      {
        id: 'OD-001',
        accountNumber: 'LOAN-2023-021',
        customerName: 'Michael Brown',
        loanAmount: 15000,
        dueAmount: 1800,
        dueDate: '2023-06-01T00:00:00',
        status: 'Overdue',
        contactNumber: '+27823456789',
        email: 'michael.b@example.com',
        daysOverdue: 14,
        lastPaymentDate: '2023-05-15T00:00:00',
        lastPaymentAmount: 1800
      },
      {
        id: 'OD-002',
        accountNumber: 'LOAN-2023-024',
        customerName: 'Sarah Williams',
        loanAmount: 8000,
        dueAmount: 950,
        dueDate: '2023-05-28T00:00:00',
        status: 'Critically Overdue',
        contactNumber: '+27761122334',
        email: 's.williams@example.com',
        daysOverdue: 18,
        lastPaymentDate: '2023-05-10T00:00:00',
        lastPaymentAmount: 950
      },
      {
        id: 'OD-003',
        accountNumber: 'LOAN-2023-027',
        customerName: 'James Wilson',
        loanAmount: 12000,
        dueAmount: 1400,
        dueDate: '2023-05-25T00:00:00',
        status: 'Partially Paid',
        contactNumber: '+27845566778',
        email: 'j.wilson@example.com',
        daysOverdue: 21,
        lastPaymentDate: '2023-05-18T00:00:00',
        lastPaymentAmount: 700
      },
      {
        id: 'OD-004',
        accountNumber: 'LOAN-2023-031',
        customerName: 'Emma Thompson',
        loanAmount: 20000,
        dueAmount: 2500,
        dueDate: '2023-05-20T00:00:00',
        status: 'In Recovery',
        contactNumber: '+27829988776',
        email: 'emma.t@example.com',
        daysOverdue: 26,
        lastPaymentDate: '2023-05-05T00:00:00',
        lastPaymentAmount: 0
      },
      {
        id: 'OD-008',
        accountNumber: 'LOAN-2023-045',
        customerName: 'Robert Johnson',
        loanAmount: 25000,
        dueAmount: 3200,
        dueDate: '2023-05-20T00:00:00',
        status: 'Critically Overdue',
        contactNumber: '+27761234567',
        email: 'robert.j@example.com',
        daysOverdue: 26,
        lastPaymentDate: '2023-04-20T00:00:00',
        lastPaymentAmount: 3200
      }
    ],
    totalElements: 8
  });

  // Fetch overdue collections
  const fetchOverdueCollections = async () => {
    setIsLoading(true);
    try {
      const response = await getOverdueCollections({
        page: 0,
        size: ALL_OVERDUE_PAGE_SIZE,
        status: filterStatus as any,
        customerName: customerName || undefined,
        minDaysOverdue: filterDaysOverdue
      });

      setOverdueCollections({
        content: response.content || [],
        totalElements: (response.content || []).length
      });
    } catch (error) {
      console.error('Error fetching overdue collections:', error);
      toast.error('Failed to fetch overdue collections');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await getOverdueCollectionStats();
      setStats(response);
    } catch (error) {
      console.error('Error fetching overdue collection stats:', error);
      toast.error('Failed to fetch collection statistics');
    }
  };

  // Fetch data when filters or pagination changes
  useEffect(() => {
    fetchOverdueCollections();
    fetchStats();
  }, [filterStatus, customerName, filterDaysOverdue]);

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Overdue':
        return 'warning';
      case 'Critically Overdue':
        return 'danger';
      case 'In Recovery':
        return 'dark';
      case 'Partially Paid':
        return 'info';
      default:
        return 'secondary';
    }
  };

  // Handle opening early repayment modal
  const handleOpenEarlyRepaymentModal = async (collection: ApiOverdueCollection) => {
    console.log('Opening early repayment modal for collection:', collection);
    setSelectedCollection(collection);
    setShowEarlyRepaymentModal(true);
    setEarlyRepaymentCalculation(null); // Reset calculation

    try {
      // Get loan account details first
      const loanAccountResponse = await getLedgerAccountById(collection.loanAccountId || '');
      console.log('Loan account loaded:', loanAccountResponse);
      setLoanAccount(loanAccountResponse);

      // Only show the modal after loan account is loaded
      const modalElement = document.getElementById('OverdueCollectionEarlyRepayment');
      if (modalElement) {
        const modal = new Modal(modalElement);
        modal.show();
      }
    } catch (error) {
      console.error('Error preparing early repayment:', error);
      toast.error('Failed to prepare early repayment');
      // Close modal if there's an error
      const modalElement = document.getElementById('OverdueCollectionEarlyRepayment');
      if (modalElement) {
        const modal = Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        }
      }
      setShowEarlyRepaymentModal(false);
    }
  };

  // Calculate early repayment amount
  const calculateEarlyRepaymentAmount = async () => {
    if (!selectedCollection || !loanAccount) return;

    setIsCalculatingEarlyRepayment(true);
    try {
      getEarlyRepaymentQuoteMutation.mutate({
        data: {
          accountId: selectedCollection.loanAccountId || '',
          quoteDate: toLocalDateString()
        }
      });
    } catch (error) {
      console.error('Error calculating early repayment:', error);
      setIsCalculatingEarlyRepayment(false);
    }
  };

  // Update early repayment calculation when mutation succeeds
  useEffect(() => {
    if (getEarlyRepaymentQuoteMutation.isSuccess && getEarlyRepaymentQuoteMutation.data) {
      setEarlyRepaymentCalculation(getEarlyRepaymentQuoteMutation.data);
      setIsCalculatingEarlyRepayment(false);

      // Set default form values
      const defaultValues = {
        valueDate: toLocalDateString(),
        externalRef: '',
        remarks: ''
      };
      resetEarlyRepayment(defaultValues);
    }
  }, [getEarlyRepaymentQuoteMutation.isSuccess, getEarlyRepaymentQuoteMutation.data, resetEarlyRepayment]);

  // Handle early repayment submission
  const handleProcessEarlyRepayment = async (data: EarlyRepaymentFormData) => {
    if (!selectedCollection || !loanAccount) {
      setAlert({
        show: true,
        type: 'error',
        message: 'No collection or loan account selected'
      });
      return;
    }

    const earlyRepaymentRequest = {
      accountId: selectedCollection.loanAccountId || '',
      valueDate: data.valueDate,
      idempotencyKey: `early-repayment-${selectedCollection.loanAccountId}-${Date.now()}`,
      externalRef: data.externalRef || undefined,
      remarks: data.remarks || undefined
    };

    recordEarlyRepaymentMutation.mutate(
      { data: earlyRepaymentRequest },
      {
        onSuccess: () => {
          // Close the modal
          const modalElement = document.getElementById('OverdueCollectionEarlyRepayment');
          if (modalElement) {
            const modal = Modal.getInstance(modalElement);
            if (modal) {
              modal.hide();
            }
          }

          // Reset form and state
          resetEarlyRepayment();
          setSelectedCollection(null);
          setShowEarlyRepaymentModal(false);
          setLoanAccount(null);
          setEarlyRepaymentCalculation(null);

          // Show success message
          setAlert({
            show: true,
            type: 'success',
            message: 'Early repayment processed successfully'
          });
        },
        onError: (error) => {
          console.error('Error processing early repayment:', error);
          setAlert({
            show: true,
            type: 'error',
            message: 'Failed to process early repayment. Please try again.'
          });
        }
      }
    );
  };

  // Update handleOpenPaymentModal to properly set form values
  const handleOpenPaymentModal = (collection: ApiOverdueCollection) => {
    console.log('Opening payment modal for collection:', collection);
    setSelectedCollection(collection);

    // Reset form and set new values
    reset({
      paymentAmount: collection.dueAmount?.toString(),
      valueDate: collection.dueDate,
      externalRef: '',
      remarks: ''
    });

    setShowPaymentModal(true);

    const modalElement = document.getElementById('OverdueCollectionPayment');
    if (modalElement) {
      const modal = Modal.getInstance(modalElement);
      if (modal) {
        modal.show();
      } else {
        const newModal = new Modal(modalElement);
        newModal.show();
      }
    }
  };

  // Update handleProcessPayment to handle the validated data
  const handleProcessPayment = async (data: PaymentFormData) => {
    if (!selectedCollection) {
      setAlert({
        show: true,
        type: 'error',
        message: 'No collection selected'
      });
      return;
    }

    // Validate the data
    const validatedData = paymentFormSchema.parse(data);

    const paymentRequest = {
      accountId: selectedCollection.loanAccountId || '',
      amount: Number(validatedData.paymentAmount),
      valueDate: validatedData.valueDate,
      idempotencyKey: `payment-${selectedCollection.loanAccountId}-${Date.now()}`,
      externalRef: validatedData.externalRef || undefined,
      remarks: validatedData.remarks || undefined
    };

    recordPaymentMutation.mutate(
      { data: paymentRequest },
      {
        onSuccess: () => {
          // Close the modal
          const modalElement = document.getElementById('OverdueCollectionPayment');
          if (modalElement) {
            const modal = Modal.getInstance(modalElement);
            if (modal) {
              modal.hide();
            } else {
              const newModal = new Modal(modalElement);
              newModal.hide();
            }
          }

          // Reset form and state
          reset();
          setSelectedCollection(null);
          setShowPaymentModal(false);

          // Show success message
          setAlert({
            show: true,
            type: 'success',
            message: 'Payment processed successfully'
          });
        },
        onError: (error) => {
          console.error('Error processing payment:', error);
          if (error instanceof z.ZodError) {
            // Handle validation errors
            const errorMessages = error.errors.map(err => err.message).join(', ');
            setAlert({
              show: true,
              type: 'error',
              message: `Validation error: ${errorMessages}`
            });
          } else {
            setAlert({
              show: true,
              type: 'error',
              message: 'Failed to process payment. Please try again.'
            });
          }
        }
      }
    );
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number) => {
    setSize(newPerPage);
    setPage(1);
  };

  // Handle customer name search
  const handleCustomerNameSearch = (name: string) => {
    setCustomerName(name);
    setPage(1); // Reset to first page when search changes
  };

  // Handle filter change
  const handleFilterChange = (status: string | undefined) => {
    setFilterStatus(status);
    setPage(1); // Reset to first page when filter changes
  };

  // Handle overdue days filter change
  const handleDaysOverdueFilterChange = (days: number | undefined) => {
    setFilterDaysOverdue(days);
    setPage(1); // Reset to first page when filter changes
  };

  // Define table columns
  const columns = [
    {
      name: 'Account Number',
      selector: (row: ApiOverdueCollection) => row.accountNumber || '',
      sortable: true,
    },
    {
      name: 'Customer',
      selector: (row: ApiOverdueCollection) => row.customerName || '',
      sortable: true,
    },
    {
      name: 'Due Date / Amount',
      selector: (row: ApiOverdueCollection) => row.dueDate || '',
      sortable: true,
      cell: (row: ApiOverdueCollection) => (
        <div>
          <div>{formatDate(row.dueDate || '')}</div>
          <small className="text-muted">{formatCurrency(row.dueAmount || 0)}</small>
        </div>
      ),
    },
    {
      name: 'Total Outstanding',
      selector: (row: ApiOverdueCollection) => row.totalOutstandingAmount || 0,
      sortable: true,
      cell: (row: ApiOverdueCollection) => (
        <div className="text-end fw-bold text-danger">
          {formatCurrency(row.totalOutstandingAmount || 0)}
        </div>
      ),
    },
    {
      name: 'Days Overdue',
      selector: (row: ApiOverdueCollection) => row.daysOverdue || 0,
      sortable: true,
      cell: (row: ApiOverdueCollection) => (
        <span className="text-danger fw-bold">
          {row.daysOverdue}
        </span>
      ),
    },
    {
      name: 'Last Payment',
      selector: (row: ApiOverdueCollection) => row.lastPaymentDate || '',
      sortable: true,
      cell: (row: ApiOverdueCollection) => (
        <div>
          <div>{formatDate(row.lastPaymentDate || '')}</div>
          <small className="text-muted">{formatCurrency(row.lastPaymentAmount || 0)}</small>
        </div>
      ),
    },
    {
      name: 'Status',
      selector: (row: ApiOverdueCollection) => row.status || '',
      sortable: true,
      cell: (row: ApiOverdueCollection) => (
        <span className={`badge bg-${getStatusColor(row.status || '')}`}>
          {row.status}
        </span>
      ),
    },
    {
      name: 'Actions',
      cell: (row: ApiOverdueCollection) => (
        <Dropdown align="end">
          <Dropdown.Toggle variant="light" size="sm" id={`actions-${row.id}`} className="rounded-pill d-flex align-items-center no-caret">
            <span>Actions</span>
            <i className="fa fa-caret-down ms-2"></i>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item className="action-pay" onClick={() => handleOpenPaymentModal(row)}>
              <i className="fa fa-credit-card me-2"></i>
              Process Payment
            </Dropdown.Item>
            <Dropdown.Item className="action-early" onClick={() => handleOpenEarlyRepaymentModal(row)}>
              <i className="fa fa-handshake-o me-2"></i>
              Early Repayment
            </Dropdown.Item>
            <Dropdown.Item className="action-reminder" onClick={() => setReminderTarget({
              accountId: row.loanAccountId || '',
              accountNumber: row.accountNumber,
              customerName: row.customerName,
            })}>
              <i className="fa fa-bell me-2"></i>
              Send Reminder
            </Dropdown.Item>
            <Dropdown.Item className="action-reminder" onClick={() => navigate(`/loan-accounts/${row.loanAccountId}#follow-up`)}>
              <i className="fa fa-phone me-2"></i>
              Log Contact / Promise
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item className="action-view" onClick={() => navigate(`/loan-accounts/${row.loanAccountId}`)}>
              <i className="fa fa-eye me-2"></i>
              View Loan Account
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ),
    },
  ];

  // Who each overdue account belongs to, so promises on the panel show the customer's name
  const customerNamesByAccount = useMemo(() => {
    const names: Record<string, string> = {};
    overdueCollections.content.forEach((row) => {
      if (row.loanAccountId && row.customerName) names[row.loanAccountId] = row.customerName;
    });
    return names;
  }, [overdueCollections]);

  // Calculate statistics for dashboard cards
  const calculateStats = () => {
    // Count statistics based on collection status
    const total = overdueCollectionsData.content.length;
    const overdue = overdueCollectionsData.content.filter(item => item.status === "Overdue").length;
    const critical = overdueCollectionsData.content.filter(item => item.status === "Critically Overdue").length;
    const recovery = overdueCollectionsData.content.filter(item => item.status === "In Recovery").length;
    const partial = overdueCollectionsData.content.filter(item => item.status === "Partially Paid").length;

    return {
      total,
      overdue,
      critical,
      recovery,
      partial
    };
  };

  const statsData = useMemo(() => calculateStats(), [overdueCollectionsData]);

  // Filter collections by status if filter is applied
  useEffect(() => {
    if (filterStatus) {
      // This would be an API call in a real application
      // For now, we'll just filter the dummy data
      const filteredContent = overdueCollectionsData.content.filter(
        item => item.status === filterStatus
      );
      setOverdueCollectionsData({
        content: filteredContent,
        totalElements: filteredContent.length
      });
    } else {
      // If no filter, reset to original data
      // This would be a reset API call in a real application
      setOverdueCollectionsData({
        content: [
          {
            id: 'OD-001',
            accountNumber: 'LOAN-2023-021',
            customerName: 'Michael Brown',
            loanAmount: 15000,
            dueAmount: 1800,
            dueDate: '2023-06-01T00:00:00',
            status: 'Overdue',
            contactNumber: '+27823456789',
            email: 'michael.b@example.com',
            daysOverdue: 14,
            lastPaymentDate: '2023-05-15T00:00:00',
            lastPaymentAmount: 1800
          },
          {
            id: 'OD-002',
            accountNumber: 'LOAN-2023-024',
            customerName: 'Sarah Williams',
            loanAmount: 8000,
            dueAmount: 950,
            dueDate: '2023-05-28T00:00:00',
            status: 'Critically Overdue',
            contactNumber: '+27761122334',
            email: 's.williams@example.com',
            daysOverdue: 18,
            lastPaymentDate: '2023-05-10T00:00:00',
            lastPaymentAmount: 950
          },
          {
            id: 'OD-003',
            accountNumber: 'LOAN-2023-027',
            customerName: 'James Wilson',
            loanAmount: 12000,
            dueAmount: 1400,
            dueDate: '2023-05-25T00:00:00',
            status: 'Partially Paid',
            contactNumber: '+27845566778',
            email: 'j.wilson@example.com',
            daysOverdue: 21,
            lastPaymentDate: '2023-05-18T00:00:00',
            lastPaymentAmount: 700
          },
          {
            id: 'OD-004',
            accountNumber: 'LOAN-2023-031',
            customerName: 'Emma Thompson',
            loanAmount: 20000,
            dueAmount: 2500,
            dueDate: '2023-05-20T00:00:00',
            status: 'In Recovery',
            contactNumber: '+27829988776',
            email: 'emma.t@example.com',
            daysOverdue: 26,
            lastPaymentDate: '2023-05-05T00:00:00',
            lastPaymentAmount: 0
          },
          {
            id: 'OD-008',
            accountNumber: 'LOAN-2023-045',
            customerName: 'Robert Johnson',
            loanAmount: 25000,
            dueAmount: 3200,
            dueDate: '2023-05-20T00:00:00',
            status: 'Critically Overdue',
            contactNumber: '+27761234567',
            email: 'robert.j@example.com',
            daysOverdue: 26,
            lastPaymentDate: '2023-04-20T00:00:00',
            lastPaymentAmount: 3200
          }
        ],
        totalElements: 8
      });
    }
  }, [filterStatus]);

  return (
    <div>
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                <li className="breadcrumb-item"><Link className="text-secondary" to="/collections">Collections</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Overdue Collections</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box">
                <h4 className="page-title">Overdue Collections</h4>
              </div>
            </div>
          </div>

          {/* Stats Cards - Compact Version */}
          <div className="row g-3 mb-4">
            <div className="col-6 col-md-3">
              <div className="card shadow-sm h-100">
                <div className="card-body d-flex align-items-center px-3 py-2">
                  <div className="avatar rounded-circle me-3 bg-light">
                    <i className="fa fa-chart-bar text-primary"></i>
                  </div>
                  <div>
                    <h6 className="card-title mb-0 small text-muted">Total Overdue</h6>
                    <h3 className="mb-0 fs-4 fw-bold">{stats.total}</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card shadow-sm h-100">
                <div className="card-body d-flex align-items-center px-3 py-2">
                  <div className="avatar rounded-circle me-3 bg-light">
                    <i className="fa fa-exclamation-circle text-warning"></i>
                  </div>
                  <div>
                    <h6 className="card-title mb-0 small text-muted">Overdue</h6>
                    <h3 className="mb-0 fs-4 fw-bold">{stats.overdue}</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card shadow-sm h-100">
                <div className="card-body d-flex align-items-center px-3 py-2">
                  <div className="avatar rounded-circle me-3 bg-light">
                    <i className="fa fa-exclamation-triangle text-danger"></i>
                  </div>
                  <div>
                    <h6 className="card-title mb-0 small text-muted">Critical</h6>
                    <h3 className="mb-0 fs-4 fw-bold">{stats.critical}</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card shadow-sm h-100">
                <div className="card-body d-flex align-items-center px-3 py-2">
                  <div className="avatar rounded-circle me-3 bg-light">
                    <i className="fa fa-hand-holding-usd text-success"></i>
                  </div>
                  <div>
                    <h6 className="card-title mb-0 small text-muted">Partially Paid</h6>
                    <h3 className="mb-0 fs-4 fw-bold">{stats.partial}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">Filter by Status</label>
                      <select
                        className="form-select"
                        value={filterStatus || ''}
                        onChange={(e) => handleFilterChange(e.target.value === '' ? undefined : e.target.value)}
                      >
                        <option value="">All Overdue</option>
                        <option value="Overdue">Overdue (&lt; 30 days)</option>
                        <option value="Critically Overdue">Critically Overdue</option>
                        <option value="In Recovery">In Recovery</option>
                        <option value="Partially Paid">Partially Paid</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Min Days Overdue</label>
                      <select
                        className="form-select"
                        value={filterDaysOverdue || ''}
                        onChange={(e) => handleDaysOverdueFilterChange(e.target.value === '' ? undefined : Number(e.target.value))}
                      >
                        <option value="">Any</option>
                        <option value="15">15+ days</option>
                        <option value="30">30+ days</option>
                        <option value="45">45+ days</option>
                        <option value="60">60+ days</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Customer Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name"
                        value={customerName}
                        onChange={(e) => handleCustomerNameSearch(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Application ID</label>
                      <input type="text" className="form-control" placeholder="Search by ID" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Promises to pay to follow up (US-36) */}
          <PromisesToPayPanel customerNames={customerNamesByAccount} />

          {/* Overdue Collections Table */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="card-title mb-0">Overdue Collections</h6>
                  <div className="dropdown">
                    <button className="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" id="dropdownMenuButton" data-bs-toggle="dropdown" aria-expanded="false">
                      <i className="fa fa-download me-1"></i>Export
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                      <li><a className="dropdown-item" href="#">Export as CSV</a></li>
                      <li><a className="dropdown-item" href="#">Export as Excel</a></li>
                      <li><a className="dropdown-item" href="#">Export as PDF</a></li>
                    </ul>
                  </div>
                </div>
                <div className="card-body">
                  {isLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading overdue collections...</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <DataTable
                        columns={columns}
                        data={overdueCollections.content}
                        pagination
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        paginationPerPage={size}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        highlightOnHover
                        striped
                        responsive
                        progressPending={isLoading}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <div className="modal fade modal-slide-in-left" id="OverdueCollectionPayment" tabIndex={-1} aria-labelledby="OverdueCollectionPaymentLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-vertical modal-dialog-scrollable" style={{ maxWidth: '900px', width: '90%' }}>
          <div className="modal-content rounded-0">
            <div className="modal-header bg-light border-bottom">
              <h5 className="modal-title d-flex align-items-center" id="OverdueCollectionPaymentLabel">
                <i className="fa fa-money-bill me-2 text-primary"></i>
                Process Payment
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body custom_scroll p-4" style={{ minHeight: 0 }}>
              {selectedCollection && (
                <FormProvider {...methods}>
                  <form onSubmit={handleSubmit(handleProcessPayment)}>
                    <div className="form-section">
                      <h6 className="form-section-title">Payment Summary</h6>
                      <p className="text-muted mb-4">
                        Review the payment details before processing. All fields marked with * are required.
                      </p>

                      {/* Summary Cards */}
                      <div className="row g-3 mb-4">
                        <div className="col-md-6">
                          <div className="card h-100 border-0 shadow-sm">
                            <div className="card-body">
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm me-3">
                                  <div className="avatar-title bg-soft-primary text-primary rounded-circle">
                                    <i className="fa fa-user"></i>
                                  </div>
                                </div>
                                <div>
                                  <h6 className="text-muted mb-1 small">Customer Name</h6>
                                  <div className="h6 fw-bold mb-0">{selectedCollection.customerName}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="card h-100 border-0 shadow-sm">
                            <div className="card-body">
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm me-3">
                                  <div className="avatar-title bg-soft-warning text-warning rounded-circle">
                                    <i className="fa fa-coins"></i>
                                  </div>
                                </div>
                                <div>
                                  <h6 className="text-muted mb-1 small">Due Amount</h6>
                                  <div className="h6 fw-bold mb-0">{formatCurrency(selectedCollection.dueAmount || 0)}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Payment Details Section */}
                      <div className="form-section">
                        <h6 className="form-section-title">Payment Details</h6>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="paymentAmount" className="form-label">
                                Payment Amount <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <i className="fa fa-rand text-muted"></i>
                                </span>
                                <input
                                  type="number"
                                  id="paymentAmount"
                                  className={`form-control ${errors.paymentAmount ? 'is-invalid' : ''}`}
                                  placeholder="Enter payment amount"
                                  {...register('paymentAmount', {
                                    required: 'Payment amount is required',
                                    min: {
                                      value: 0.01,
                                      message: 'Payment amount must be greater than 0'
                                    }
                                  })}
                                  min="0.01"
                                  step="0.01"
                                />
                              </div>
                              {errors.paymentAmount && (
                                <div className="invalid-feedback">{errors.paymentAmount.message}</div>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="valueDate" className="form-label">
                                Payment Date <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <i className="fa fa-calendar text-muted"></i>
                                </span>
                                <input
                                  type="date"
                                  id="valueDate"
                                  className={`form-control ${errors.valueDate ? 'is-invalid' : ''}`}
                                  {...register('valueDate', {
                                    required: 'Payment date is required'
                                  })}
                                  defaultValue={toLocalDateString()}
                                />
                              </div>
                              {errors.valueDate && (
                                <div className="invalid-feedback">{errors.valueDate.message}</div>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="externalRef" className="form-label">
                                External Reference
                              </label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <i className="fa fa-hashtag text-muted"></i>
                                </span>
                                <input
                                  type="text"
                                  id="externalRef"
                                  className="form-control"
                                  placeholder="Enter external reference"
                                  {...register('externalRef')}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12">
                            <div className="form-group">
                              <label htmlFor="remarks" className="form-label">
                                Remarks
                              </label>
                              <textarea
                                id="remarks"
                                className="form-control"
                                placeholder="Enter transaction remarks"
                                rows={3}
                                {...register('remarks')}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="modal-footer border-top mt-4 pt-3">
                        <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={recordPaymentMutation.isPending}
                        >
                          {recordPaymentMutation.isPending ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Processing...
                            </>
                          ) : (
                            'Process Payment'
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </FormProvider>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Early Repayment Modal */}
      <div className="modal fade modal-slide-in-left" id="OverdueCollectionEarlyRepayment" tabIndex={-1} aria-labelledby="OverdueCollectionEarlyRepaymentLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-vertical" style={{ maxWidth: '900px', width: '90%', height: '100vh', margin: 0 }}>
          <div className="modal-content rounded-0 d-flex flex-column" style={{ height: '100%' }}>
            <div className="modal-header bg-light border-bottom">
              <h5 className="modal-title d-flex align-items-center" id="OverdueCollectionEarlyRepaymentLabel">
                <i className="fa fa-handshake-o me-2 text-warning"></i>
                Process Early Repayment
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body p-4 overflow-auto flex-grow-1">
              {!earlyRepaymentCalculation ? (
                <div className="text-center py-5">
                  <div className="mb-4">
                    <div className="avatar-lg mx-auto mb-3">
                      <div className="avatar-title bg-soft-warning text-warning rounded-circle">
                        <i className="fa fa-calculator fa-2x"></i>
                      </div>
                    </div>
                    <h5 className="mb-2">Calculate Early Repayment Amount</h5>
                    <p className="text-muted mb-4">
                      Click the button below to calculate the total amount required for early repayment, including outstanding principal, interest, and any applicable penalties.
                    </p>
                  </div>
                  <button
                    className="btn btn-warning btn-lg px-4"
                    onClick={calculateEarlyRepaymentAmount}
                    disabled={isCalculatingEarlyRepayment}
                  >
                    {isCalculatingEarlyRepayment ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Calculating...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-calculator me-2"></i>
                        Calculate Early Repayment Amount
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <FormProvider {...earlyRepaymentMethods}>
                  <form onSubmit={handleSubmitEarlyRepayment(handleProcessEarlyRepayment)} className="d-flex flex-column">
                    <div className="form-section flex-grow-1">
                      <h6 className="form-section-title">Early Repayment Summary</h6>
                      <p className="text-muted mb-4">
                        Review the early repayment details before processing. All fields marked with * are required.
                      </p>

                      {/* Summary Cards */}
                      <div className="row g-3 mb-4">
                        <div className="col-md-6">
                          <div className="card h-100 border-0 shadow-sm">
                            <div className="card-body">
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm me-3">
                                  <div className="avatar-title bg-soft-primary text-primary rounded-circle">
                                    <i className="fa fa-user"></i>
                                  </div>
                                </div>
                                <div>
                                  <h6 className="text-muted mb-1 small">Customer Name</h6>
                                  <div className="h6 fw-bold mb-0">{selectedCollection?.customerName}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="card h-100 border-0 shadow-sm">
                            <div className="card-body">
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm me-3">
                                  <div className="avatar-title bg-soft-warning text-warning rounded-circle">
                                    <i className="fa fa-coins"></i>
                                  </div>
                                </div>
                                <div>
                                  <h6 className="text-muted mb-1 small">Total Payoff Amount</h6>
                                  <div className="h6 fw-bold mb-0">{formatCurrency(earlyRepaymentCalculation?.totalPayoffAmount || 0)}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Breakdown Section */}
                      <div className="card mb-4">
                        <div className="card-body">
                          <h6 className="card-title mb-3">Payment Breakdown</h6>
                          <div className="row g-3">
                            <div className="col-md-4">
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm me-3">
                                  <div className="avatar-title bg-soft-info text-info rounded-circle">
                                    <i className="fa fa-money-bill"></i>
                                  </div>
                                </div>
                                <div>
                                  <h6 className="text-muted mb-1 small">Outstanding Principal</h6>
                                  <div className="h6 fw-bold mb-0">{formatCurrency(earlyRepaymentCalculation?.principalOutstanding || 0)}</div>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm me-3">
                                  <div className="avatar-title bg-soft-success text-success rounded-circle">
                                    <i className="fa fa-percentage"></i>
                                  </div>
                                </div>
                                <div>
                                  <h6 className="text-muted mb-1 small">Accrued Interest</h6>
                                  <div className="h6 fw-bold mb-0">{formatCurrency(earlyRepaymentCalculation?.accruedInterest || 0)}</div>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm me-3">
                                  <div className="avatar-title bg-soft-danger text-danger rounded-circle">
                                    <i className="fa fa-exclamation-circle"></i>
                                  </div>
                                </div>
                                <div>
                                  <h6 className="text-muted mb-1 small">Penalties & Fees</h6>
                                  <div className="h6 fw-bold mb-0">{formatCurrency((earlyRepaymentCalculation?.unpaidPenalties || 0) + (earlyRepaymentCalculation?.unpaidFees || 0))}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Payment Details Section */}
                      <div className="form-section">
                        <h6 className="form-section-title">Payment Details</h6>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="earlyRepaymentValueDate" className="form-label">
                                Payment Date <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <i className="fa fa-calendar text-muted"></i>
                                </span>
                                <input
                                  type="date"
                                  id="earlyRepaymentValueDate"
                                  className={`form-control ${earlyRepaymentErrors.valueDate ? 'is-invalid' : ''}`}
                                  {...registerEarlyRepayment('valueDate', {
                                    required: 'Payment date is required'
                                  })}
                                  defaultValue={toLocalDateString()}
                                />
                              </div>
                              {earlyRepaymentErrors.valueDate && (
                                <div className="invalid-feedback">{earlyRepaymentErrors.valueDate.message}</div>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="earlyRepaymentExternalRef" className="form-label">
                                External Reference
                              </label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <i className="fa fa-hashtag text-muted"></i>
                                </span>
                                <input
                                  type="text"
                                  id="earlyRepaymentExternalRef"
                                  className="form-control"
                                  placeholder="Enter external reference"
                                  {...registerEarlyRepayment('externalRef')}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12">
                            <div className="form-group">
                              <label htmlFor="earlyRepaymentRemarks" className="form-label">
                                Remarks
                              </label>
                              <textarea
                                id="earlyRepaymentRemarks"
                                className="form-control"
                                placeholder="Enter transaction remarks"
                                rows={3}
                                {...registerEarlyRepayment('remarks')}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="modal-footer border-top mt-4 pt-3 position-sticky bottom-0 bg-white">
                        <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                        <button
                          type="submit"
                          className="btn btn-warning"
                          disabled={recordEarlyRepaymentMutation.isPending || getEarlyRepaymentQuoteMutation.isPending}
                        >
                          {recordEarlyRepaymentMutation.isPending || getEarlyRepaymentQuoteMutation.isPending ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Processing...
                            </>
                          ) : (
                            'Process Early Repayment'
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </FormProvider>
              )}
            </div>
          </div>
        </div>
      </div>

      <SendReminderModal target={reminderTarget} onHide={() => setReminderTarget(null)} />
    </div>
  );
};

export default OverdueCollections;
